<template>
  <Modal
    v-model:visible="isVisible"
    class="incident-modal"
    :header="header"
    size="md"
    @keypress.enter="onSave"
    @keypress.esc="onCancel"
  >
    <div class="flex flex-gap-1">
      <div class="w-6">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="incident-type"
          class="field"
          :label="t('common.type')"
          :errors="v$.incidentType.$errors"
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.incidentType.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            append-to="body"
            :options="types"
            option-label="name"
            option-value="naturalId"
            data-key="naturalId"
            filter
            :placeholder="t('common.type')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="incident-severity"
          class="field"
          :label="t('events.severity')"
          :errors="v$.severity.$errors"
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.severity.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            append-to="body"
            :options="severityOptions"
            option-label="label"
            option-value="value"
            filter
            :placeholder="t('events.severity')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="actual-date"
          class="field"
          :label="t('shipments.dates.actualDateTime')"
          :errors="v$.actualDate.$errors"
        >
          <div class="flex flex-gap-1 incident-calendar-tz-wrapper">
            <RygenCalendar
              :id="inputId"
              v-model="incident.actualDate"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="incident.actualLocation.timeZone"
              class="w-8"
              date-format="mm/dd/yy"
              show-icon
              show-time
              hour-format="12"
              :step-minute="15"
              :placeholder="t('shipments.dates.actualDateTime')"
            />
            <TimeZoneDropdown
              v-model="incident.actualLocation.timeZone"
              :date-time-basis="incident.actualDate"
              :disabled="!!incident.relatedStopId"
              class="flex-grow-1"
              :placeholder="t('common.timezones')"
            />
          </div>
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="incident-comment"
          class="field"
          :label="t('common.comments')"
          optional
        >
          <Textarea
            :id="inputId"
            v-model="incident.comment"
            v-bind="ariaProps"
            rows="5"
            cols="60"
            :placeholder="t('common.comments')"
            @keypress.enter="$event.stopPropagation()"
            @keypress.shift.enter="onSave"
          />
        </InputWrapper>
      </div>

      <div class="w-6">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="incident-related-stop-id"
          class="field"
          :errors="v$.relatedStopId.$errors"
          :label="t('common.relatedParam', { param: t('shipments.stops') })"
        >
          <RygenDropdown
            :id="inputId"
            v-model="incident.relatedStopId"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            append-to="body"
            :options="shipmentStopOptions"
            data-key="id"
            option-label="location.locationName"
            option-value="id"
            filter
            show-clear
            :placeholder="
              t('common.relatedParam', { param: t('shipments.stops') })
            "
          >
          </RygenDropdown>
        </InputWrapper>

        <InputWrapper
          v-if="!carrierDisabled"
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="incident-carrier"
          class="field"
          :label="t('carriers.carriers')"
          :errors="v$.carrierId.$errors"
        >
          <CarrierQuickSearch
            :id="inputId"
            :model-value="selectedCarrier"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :search-type="showAllCarriers ? 'all' : 'assigned'"
            append-to="body"
            @update:model-value="onCarrierSelected"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="incident-city"
          class="field"
          :label="t('address.city')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="incident.actualLocation.city"
            v-bind="ariaProps"
            :disabled="!!incident.relatedStopId"
            :placeholder="t('address.city')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="incident-state"
          class="field"
          :label="t('address.state')"
          optional
        >
          <InputRegion
            :id="inputId"
            v-model="incident.actualLocation.state"
            v-bind="ariaProps"
            :country="incident.actualLocation.country"
            :disabled="!!incident.relatedStopId"
            append-to="body"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="incident-postal-code"
          class="field"
          :label="t('address.postalCode')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="incident.actualLocation.postalCode"
            v-bind="ariaProps"
            :disabled="!!incident.relatedStopId"
            :placeholder="t('address.postalCode')"
          />
        </InputWrapper>
      </div>
    </div>

    <RelatedStopDates
      v-if="showRelatedStopDates && relatedStop"
      :related-stop="relatedStop"
    />

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :disabled="disabled"
        :label="t('common.saveParam', { param: t('events.incidents') })"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import {
  computed,
  ComputedRef,
  getCurrentInstance,
  PropType,
  ref,
  Ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { titleCase } from '@rygen/rygen-ui/i18n';
import {
  DropdownOption,
  isShipmentDeliveryServiceIncident,
  isShipmentOriginServiceIncident,
  ServiceIncidentType,
  ServiceIncidentTypeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  Carrier,
  Shipment,
  ShipmentEventLocation,
  ShipmentServiceIncident,
  ShipmentStatus,
  ShipmentStop,
  ShipmentStopType,
} from '@/model';
import { isStatusEqualOrBefore } from '@/util';

import CarrierQuickSearch from '@/components/carrier/CarrierQuickSearch.vue';
import RelatedStopDates from '@/components/carrierPortal/event/RelatedStopDates.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import TimeZoneDropdown from '@rygen/rygen-ui/components/form/TimeZoneDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  serviceIncident: {
    type: Object as PropType<ShipmentServiceIncident>,
    default: () => new ShipmentServiceIncident(),
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  types: {
    type: Array as PropType<ServiceIncidentType[]>,
    default: () => [],
  },
  severityOptions: {
    type: Array as PropType<DropdownOption<string>[]>,
    default: () => [],
  },
  shipmentStops: {
    type: Array as PropType<ShipmentStop[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  carrierDisabled: {
    type: Boolean,
    default: () => false,
  },
  showRelatedStopDates: {
    type: Boolean,
    default: () => false,
  },
});
const {
  serviceIncident,
  shipment,
  types,
  severityOptions,
  shipmentStops,
  disabled,
  carrierDisabled,
  showRelatedStopDates,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', newVal: ShipmentServiceIncident): void;
}>();

const { t } = useI18n();
const { uid } = useUid('shipment-service-incident-modal');

const incident: Ref<ShipmentServiceIncident> = ref(
  new ShipmentServiceIncident(),
);
const rules = {
  incidentType: {
    required: required(t('common.type')),
  },
  severity: {
    required: required(t('events.severity')),
  },
  actualDate: {
    required: required(t('shipments.dates.actualDateTime')),
  },
  carrierId: {
    required: required(t('carriers.carriers')),
  },
  relatedStopId: {
    required: required(
      t('common.relatedParam', { param: t('shipments.stops') }),
    ),
  },
};
const { v$, validate } = useValidations(incident, rules, uid);

const selectedCarrier: Ref<Carrier | undefined> = ref();
// Deliberately not a ref: `ref()` would proxy the token source for no benefit.
let carrierRequest: CancelTokenSource | undefined;

const showAllCarriers = computed(() =>
  authorization.hasAuthority('spotQuote', 'allCarriers'),
);

const onCarrierSelected = (carrier?: Carrier) => {
  selectedCarrier.value = carrier;
  incident.value.carrierId = carrier?.id;
};

/**
 * The carrier picker searches server side, so it has no list to resolve an
 * already-saved carrierId against and the label would render empty.
 */
const loadSelectedCarrier = async (carrierId?: number) => {
  if (
    carrierDisabled.value ||
    !carrierId ||
    !authorization.hasAuthority('carrier', 'read')
  ) {
    selectedCarrier.value = undefined;
    return;
  }

  if (selectedCarrier.value?.id === carrierId) {
    return;
  }

  carrierRequest = axios.CancelToken.source();

  try {
    selectedCarrier.value = await api.carrier.findById(
      carrierId,
      carrierRequest,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading carrier', e);
    selectedCarrier.value = undefined;
  }
};

const shipmentStopOptions: ComputedRef<ShipmentStop[]> = computed(() => {
  if (!shipmentStops.value.length) {
    return [];
  }

  if (incident.value.incidentType) {
    const incidentType = incident.value
      .incidentType as ServiceIncidentTypeWellKnownNaturalId;
    if (isShipmentOriginServiceIncident(incidentType)) {
      return [shipmentStops.value[0]];
    } else if (isShipmentDeliveryServiceIncident(incidentType)) {
      return [shipmentStops.value[shipmentStops.value.length - 1]];
    }
  }
  return shipmentStops.value;
});

const header = computed(() => {
  if (!incident.value.id) {
    return t('common.newParam', { param: t('events.incidents') });
  }

  let title = t('common.editParam', {
    param: t('events.incidents'),
  });

  if (incident.value.incidentType) {
    title += ' - ' + titleCase(incident.value.incidentType);
  }

  return title;
});

const shipmentStopById = computed(() => {
  return shipmentStops.value.reduce(
    (acc, stop) => {
      if (stop.id) {
        acc[stop.id] = stop;
      }
      return acc;
    },
    {} as Record<number, ShipmentStop>,
  );
});

const relatedStop = computed(() => {
  return incident.value.relatedStopId
    ? shipmentStopById.value[incident.value.relatedStopId]
    : undefined;
});

const onSave = async () => {
  if (await validate()) {
    emit('save', incident.value);
    isVisible.value = false;
  }
};

const onCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

const updateIncidentStopType = () => {
  if (!incident.value.relatedStopId) {
    const isBookedOrEarlier = isStatusEqualOrBefore(
      shipment.value.status,
      ShipmentStatus.BOOKED,
    );
    incident.value.relatedStopId = shipmentStops.value.find((s) =>
      isBookedOrEarlier
        ? s.stopType === ShipmentStopType.PICKUP
        : s.stopType === ShipmentStopType.DROP_OFF,
    )?.id;
  }
};

const updateIncidentStop = (
  newRelatedStopId?: number,
  oldRelatedStopId?: number,
) => {
  const newRelatedStop = shipmentStops.value.find(
    (stop) => stop.id === newRelatedStopId,
  );
  const oldRelatedStop = shipmentStops.value.find(
    (stop) => stop.id === oldRelatedStopId,
  );

  const actualLocation =
    incident.value.actualLocation ?? new ShipmentEventLocation();
  const oldActualLocationIsEmpty =
    !actualLocation.city && !actualLocation.state && !actualLocation.postalCode;

  const oldActualLocationIsOldStop =
    !oldActualLocationIsEmpty &&
    ((oldRelatedStop?.location?.city === actualLocation.city &&
      oldRelatedStop?.location?.state === actualLocation.state) ||
      oldRelatedStop?.location?.postalCode === actualLocation.postalCode);

  const shouldUpdateLocation =
    oldActualLocationIsEmpty || oldActualLocationIsOldStop;

  if (shouldUpdateLocation) {
    incident.value.actualLocation = new ShipmentEventLocation({
      ...newRelatedStop?.location,
    });
  }
};

watch(
  serviceIncident,
  (newVal: ShipmentServiceIncident) => {
    incident.value = new ShipmentServiceIncident(newVal);
    loadSelectedCarrier(incident.value.carrierId);
    updateIncidentStopType();
    updateIncidentStop(incident.value.relatedStopId);
    v$.value.$reset();
  },
  { deep: true, immediate: true },
);

watch(shipmentStopOptions, (stopOptions: ShipmentStop[]) => {
  if (stopOptions.length === 1) {
    incident.value.relatedStopId = stopOptions[0].id;
  }
});

watch(() => incident.value.relatedStopId, updateIncidentStop);

watch(isVisible, (newVal: boolean) => {
  if (newVal) {
    const instance = getCurrentInstance();
    instance?.proxy?.$forceUpdate();
    v$.value.$reset();
    updateIncidentStop(incident.value.relatedStopId);
    return;
  }

  // The modal instance is reused for every incident, so an in-flight lookup
  // from the incident being closed would otherwise resolve onto the next one.
  carrierRequest?.cancel();
  carrierRequest = undefined;
});
</script>
